/**
 * EntityTypePill — a droppable card for an entity type that has no mapped
 * columns yet. Shown below the active sections so the user can drag columns
 * onto it to create a new section.
 *
 * Styled to visually echo the section headers above — same icon + label
 * layout, same card width — but with a dashed border and placeholder text
 * so it reads as "an empty section waiting for columns."
 *
 * When a column is dropped the pill's virtual key already participates in the
 * same DndContext as the sections, so the parent `handleDragEnd` handles the
 * move. On the next render the type has columns and transitions from pill →
 * full section automatically.
 */

import { useDroppable } from '@dnd-kit/core';

import { DropArea } from '@/lib/base/card';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';

interface EntityTypePillProps {
  entityTypeName: string;
  label: string;
  /** Droppable id — `${fileId}_virtual_${entityTypeName}`. */
  virtualKey: string;
}

export function EntityTypePill({
  entityTypeName,
  label,
  virtualKey,
}: EntityTypePillProps) {
  const { setNodeRef, isOver } = useDroppable({ id: virtualKey });
  const Icon = getEntityTypeIconComponent(entityTypeName);

  return (
    <DropArea
      nodeRef={setNodeRef}
      isOver={isOver}
      showBorder
      ariaLabel={`Drop zone for ${label}`}
    >
      <div className='flex items-center gap-3 bg-bg-secondary px-4 py-3 rounded-md'>
        <Icon
          className='size-4 shrink-0 text-fg-quaternary'
          aria-hidden='true'
        />
        <span className='text-sm font-medium text-fg-secondary'>{label}</span>
        <span className='text-xs text-fg-quaternary'>
          Drop columns here to include
        </span>
      </div>
    </DropArea>
  );
}
